import React, { useState } from 'react';

import type { AIError } from '@aline/common';
import { aiErrorSchema } from '@aline/common';
import { Collapse, Typography } from '@mui/material';
import { ChatChevronIcon } from 'src/components/icons/ChatChevronIcon';
import { ErrorIcon } from 'src/components/icons/ErrorIcon';
import { makeStyles } from 'tss-react/mui';

import type { MarkdownComponentProps } from '../../ChatPanelMarkdown';

const useStyles = makeStyles()(theme => ({
  contentContainer: {
    flex: '1 1 auto',
  },
  detailsButton: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    gap: 2,
    marginTop: theme.spacing(1),
  },
  detailsText: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    textDecoration: 'underline',
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  message: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  root: {
    border: `2px solid ${theme.palette.error.main}`,
    borderRadius: 8,
    display: 'flex',
    gap: theme.spacing(1),
    margin: theme.spacing(1, 0),
    padding: theme.spacing(2),
  },
  stack: {
    color: '#666',
    fontSize: 12,
    marginTop: theme.spacing(0.5),
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
  },
}));

export const CodeErrorElement = React.memo(function ToolCallElement({
  children,
}: MarkdownComponentProps): JSX.Element | null {
  const { classes } = useStyles();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const code = React.Children.toArray(children).join('');
  const errorData = parseErrorPayload(code);

  return (
    <div className={classes.root}>
      <div className={classes.iconContainer}>
        <ErrorIcon style={{ height: 20, width: 20 }} />
      </div>
      <div className={classes.contentContainer}>
        <Typography className={classes.title}>{errorData.title}</Typography>
        <Typography className={classes.message}>{errorData.message}</Typography>
        <div className={classes.detailsButton} onClick={() => setIsOpen(!isOpen)}>
          <Typography className={classes.detailsText}>Error details</Typography>
          <ChatChevronIcon
            height={14}
            style={isOpen ? { transform: 'rotate(90deg)' } : undefined}
            width={14}
          />
        </div>
        <Collapse in={isOpen}>
          <Typography className={classes.stack}>{errorData.stack}</Typography>
        </Collapse>
      </div>
    </div>
  );
});

/**
 * Robustly turn a json-error fence body into an `AIError` for the card.
 *
 * The server writes the fence as a single atomic snapshot, but live
 * subscription updates can deliver mid-stream snapshots where the body
 * is still being assembled — JSON.parse on a half-written object throws
 * an uncaught SyntaxError that takes down the whole ChatPanel via the
 * ErrorBoundary above us. Guard both layers (JSON.parse and the Zod
 * schema check) and fall back to surfacing the raw code so the user
 * still sees something useful instead of an empty error boundary.
 */
function parseErrorPayload(code: string): AIError {
  const unknown: AIError = {
    message: code.trim() === '' ? 'No error details available yet.' : code,
    stack: '',
    title: 'Unknown error',
  };
  let parsed: unknown;
  try {
    parsed = JSON.parse(code);
  } catch {
    return unknown;
  }
  const errorResult = aiErrorSchema.safeParse(parsed);
  return errorResult.success ? errorResult.data : unknown;
}
